import React, { forwardRef } from "react";

import * as Separator from "@radix-ui/react-separator";
import { cva, VariantProps } from "class-variance-authority";

import * as theme from "./Divider.css";

export const dividerStyle = cva(theme.base, {
    variants: {
        orientation: {
            horizontal: theme.orientationHorizontal,
            vertical: theme.orientationVertical,
        },
        spacing: {
            small: "",
            medium: "",
            large: "",
        },
    },
    compoundVariants: [
        {
            orientation: "horizontal",
            spacing: "small",
            className: theme.spacingSmallHorizontal,
        },
        {
            orientation: "vertical",
            spacing: "small",
            className: theme.spacingSmallVertical,
        },
        {
            orientation: "horizontal",
            spacing: "medium",
            className: theme.spacingMediumHorizontal,
        },
        {
            orientation: "vertical",
            spacing: "medium",
            className: theme.spacingMediumVertical,
        },
        {
            orientation: "horizontal",
            spacing: "large",
            className: theme.spacingLargeHorizontal,
        },
        {
            orientation: "vertical",
            spacing: "large",
            className: theme.spacingLargeVertical,
        },
    ],
    defaultVariants: {
        orientation: "horizontal",
        spacing: "medium",
    },
});

interface DividerProps extends Omit<
    React.ComponentPropsWithoutRef<typeof Separator.Root>,
    "orientation"
> {
    orientation?: VariantProps<typeof dividerStyle>["orientation"];
    spacing?: VariantProps<typeof dividerStyle>["spacing"];
}

export const Divider = forwardRef<
    React.ElementRef<typeof Separator.Root>,
    DividerProps
>(
    (
        {
            className,
            orientation = "horizontal",
            spacing = "medium",
            children,
            ...props
        },
        ref
    ) => {
        const hasChildren = !!children && orientation === "horizontal";
        return hasChildren ? (
            <div className={theme.dividerWithText}>
                <Separator.Root
                    ref={ref}
                    className={dividerStyle({
                        orientation,
                        spacing,
                        className,
                    })}
                    orientation={orientation}
                    {...props}
                />
                {children}
                <Separator.Root
                    className={dividerStyle({
                        orientation,
                        spacing,
                        className,
                    })}
                    orientation={orientation}
                    aria-hidden
                />
            </div>
        ) : (
            <Separator.Root
                ref={ref}
                className={dividerStyle({ orientation, spacing, className })}
                orientation={orientation || "horizontal"}
                {...props}
            />
        );
    }
) as React.ForwardRefExoticComponent<
    DividerProps & React.RefAttributes<React.ElementRef<typeof Separator.Root>>
> & {
    displayName?: string;
};

Divider.displayName = "Divider";
